import type { Workflow } from '~/types/workflow.types';

// Published workflow — no open draft.
export const welcomeSeriesWorkflow: Workflow = {
    workflow_id: 'welcome_series',
    version: 1,
    status: 'published',
    published_at: '2026-05-01T00:00:00.000Z',
    deleted_at: null,
    updated_at: '2026-05-01T00:00:00.000Z',
    definition: {
        name: 'Welcome Series',
        description: 'Sends a welcome message to new subscribers',
        entry_trigger: { category: 'contact', type: 'created' },
        steps: [
            {
                step_id: 'step_1',
                type: 'send',
                message_key: 'welcome_message',
                campaign_subtype: 'marketing',
                safe_sending: true,
                message: 'Welcome to our community!',
            },
            {
                step_id: 'step_2',
                type: 'mutate',
                target_path: '',
                value: null,
                actions: [
                    {
                        id: 'action_welcome_tag',
                        category: 'contact',
                        action: 'add_tag',
                        config: { tag_value: 'welcomed' },
                    },
                ],
            },
            {
                step_id: 'step_3',
                type: 'send_to_workflow',
                target_workflow_id: 're_engagement',
            },
        ],
    },
};

// Workflow with a published version and an open draft (v2 in progress).
export const helpRequestWorkflowPublished: Workflow = {
    workflow_id: 'help_request',
    version: 1,
    status: 'published',
    published_at: '2026-05-15T00:00:00.000Z',
    deleted_at: null,
    updated_at: '2026-05-15T00:00:00.000Z',
    definition: {
        name: 'Help Request Workflow',
        description: 'Responds to contacts requesting help',
        entry_trigger: { category: 'message', type: 'received' },
        exit_criteria: {
            enabled: true,
            groups: [
                {
                    id: 'exit_group_resolved',
                    kind: 'success',
                    expressions: [
                        { id: 'exit_expr_tag', type: 'tags', operator: 'has_any', values: ['resolved'] },
                        { id: 'exit_expr_reply', type: 'engagement_inbound_message_since_entry' },
                    ],
                },
                {
                    id: 'exit_group_optout',
                    kind: 'failure',
                    expressions: [{ id: 'exit_expr_optout', type: 'custom_attribute_exists', key: 'opted_out' }],
                },
            ],
        },
        steps: [
            {
                step_id: 'step_1',
                type: 'condition',
                condition_branches: [
                    {
                        id: 'branch_if',
                        logic: 'and',
                        target_step: 'step_2',
                        rules: [
                            {
                                id: 'rule_help_intent',
                                category: 'event',
                                field: 'event.payload.intent',
                                operator: 'equals',
                                value: 'HELP',
                            },
                        ],
                        groups: [
                            {
                                id: 'grp_branch_if',
                                rules: [
                                    {
                                        id: 'rule_help_intent',
                                        category: 'event',
                                        field: 'event.payload.intent',
                                        operator: 'equals',
                                        value: 'HELP',
                                    },
                                ],
                            },
                        ],
                    },
                    { id: 'branch_else', logic: 'and', rules: [], groups: [] },
                ],
            },
            {
                step_id: 'step_2',
                type: 'send',
                message_key: 'help_response',
                campaign_subtype: 'marketing',
                safe_sending: true,
                message: 'We are here to help!',
            },
        ],
    },
};

export const helpRequestWorkflowDraft: Workflow = {
    workflow_id: 'help_request',
    version: 2,
    status: 'draft',
    published_at: null,
    deleted_at: null,
    updated_at: '2026-06-01T00:00:00.000Z',
    definition: {
        name: 'Help Request Workflow',
        description: 'Responds to contacts requesting help — adding tag step',
        entry_trigger: { category: 'message', type: 'received' },
        steps: [
            {
                step_id: 'step_1',
                type: 'condition',
                condition_branches: [
                    {
                        id: 'branch_if',
                        logic: 'and',
                        target_step: 'step_2',
                        rules: [
                            {
                                id: 'rule_help_intent',
                                category: 'event',
                                field: 'event.payload.intent',
                                operator: 'equals',
                                value: 'HELP',
                            },
                        ],
                        groups: [
                            {
                                id: 'grp_branch_if',
                                rules: [
                                    {
                                        id: 'rule_help_intent',
                                        category: 'event',
                                        field: 'event.payload.intent',
                                        operator: 'equals',
                                        value: 'HELP',
                                    },
                                ],
                            },
                        ],
                    },
                    { id: 'branch_else', logic: 'and', rules: [], groups: [] },
                ],
            },
            {
                step_id: 'step_2',
                type: 'send',
                message_key: 'help_response',
                campaign_subtype: 'marketing',
                safe_sending: true,
                message: 'We are here to help!',
            },
            {
                step_id: 'step_3',
                type: 'mutate',
                target_path: '',
                value: null,
                actions: [
                    {
                        id: 'action_help_tag',
                        category: 'contact',
                        action: 'add_tag',
                        config: { tag_value: 'help_requested' },
                    },
                ],
            },
        ],
    },
};

// Draft-only workflow — never published.
export const reEngagementWorkflow: Workflow = {
    workflow_id: 're_engagement',
    version: 1,
    status: 'draft',
    published_at: null,
    deleted_at: null,
    updated_at: '2026-06-05T00:00:00.000Z',
    definition: {
        name: 'Re-engagement Campaign',
        description: 'Targets contacts who have not engaged in 30 days',
        entry_trigger: { category: 'segment', type: 'entered' },
        exit_criteria: {
            enabled: true,
            groups: [
                {
                    id: 'exit_reengaged',
                    kind: 'success',
                    expressions: [
                        { id: 'exit_reply', type: 'engagement_inbound_message_since_entry' },
                        { id: 'exit_click', type: 'engagement_link_clicked_since_entry' },
                    ],
                },
                {
                    id: 'exit_unsub',
                    kind: 'failure',
                    expressions: [
                        {
                            id: 'exit_marketing',
                            type: 'contact_field',
                            field: 'marketing_status',
                            operator: 'in',
                            values: ['unsubscribed'],
                        },
                    ],
                },
            ],
        },
        steps: [
            {
                step_id: 'step_1',
                type: 'send',
                message_key: 'reengagement_offer',
                campaign_subtype: 'marketing',
                safe_sending: true,
                message: "We miss you! Come back and check out what's new.",
            },
        ],
    },
};

// Draft-only workflow exercising every step type — wait, condition, send, compliance, mutate, send_to_workflow.
export const allStepTypesWorkflow: Workflow = {
    workflow_id: 'all_step_types',
    version: 1,
    status: 'draft',
    published_at: null,
    deleted_at: null,
    updated_at: '2026-06-10T00:00:00.000Z',
    draft_version: 1,
    definition: {
        name: 'All Step Types',
        description: '',
        entry_trigger: {
            category: 'contact',
            type: 'external_list_contact_added',
            conditions: {
                rules: [
                    {
                        id: 'rule_1781601266095_qofysho3x',
                        category: 'details',
                        field: 'state',
                        operator: 'is_not_empty',
                        value: null,
                    },
                ],
                logic: 'and',
                groups: [
                    {
                        id: 'grp_1781601266095_39pnwqlvi',
                        rules: [
                            {
                                id: 'rule_1781601266095_qofysho3x',
                                category: 'details',
                                field: 'state',
                                operator: 'is_not_empty',
                                value: null,
                            },
                        ],
                    },
                ],
            },
        },
        steps: [
            {
                step_id: 'step_2',
                type: 'wait',
                duration: '1d',
            },
            {
                step_id: 'step_1',
                type: 'condition',
                condition_branches: [
                    {
                        id: 'branch_1781276970242_3i4ar2ene',
                        logic: 'and',
                        target_step: 'step_3',
                        rules: [
                            {
                                id: 'rule_1781276974377_pm8vqzwvj',
                                category: 'details',
                                field: 'lifecycle_status',
                                operator: 'is_not_empty',
                                value: null,
                            },
                        ],
                        groups: [
                            {
                                id: 'grp_1781276974377_6qzjsl8i1',
                                rules: [
                                    {
                                        id: 'rule_1781276974377_pm8vqzwvj',
                                        category: 'details',
                                        field: 'lifecycle_status',
                                        operator: 'is_not_empty',
                                        value: null,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        id: 'branch_1781276980327_yhwxfhvzq',
                        logic: 'and',
                        target_step: 'step_4',
                        rules: [
                            {
                                id: 'rule_1781276996010_hf4ts0oz6',
                                category: 'details',
                                field: 'lifecycle_status',
                                operator: 'is_not_empty',
                                value: null,
                            },
                        ],
                        groups: [
                            {
                                id: 'grp_1781276996010_uwuo6ikn0',
                                rules: [
                                    {
                                        id: 'rule_1781276996010_hf4ts0oz6',
                                        category: 'details',
                                        field: 'lifecycle_status',
                                        operator: 'is_not_empty',
                                        value: null,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                step_id: 'step_3',
                type: 'send',
                message_key: '',
                campaign_subtype: 'marketing',
                safe_sending: true,
                message: 'Send text',
                next_step: 'step_5',
            },
            {
                step_id: 'step_4',
                type: 'compliance',
                compliance_actions: ['marketing_in', 'transactional_out'],
                message: 'You are now subscribed',
                next_step: 'step_6',
            },
            {
                step_id: 'step_5',
                type: 'mutate',
                target_path: '',
                value: null,
                actions: [
                    {
                        id: 'action_1781601211090_r3h6lie28',
                        category: 'contact',
                        action: 'add_tag',
                        config: { tag_value: 'new_tag' },
                    },
                ],
            },
            {
                step_id: 'step_6',
                type: 'send_to_workflow',
                target_workflow_id: 'new_workflow_1779976876177',
                target_workflow_name: 'Workflow entry trigger',
            },
        ],
        exit_criteria: {
            enabled: true,
            groups: [
                {
                    id: 'grp_1781262297996_f86wg5p14',
                    kind: 'neutral',
                    expressions: [
                        {
                            id: 'rule_1781262297996_qxllqmzgp',
                            type: 'contact_field',
                            field: 'entry_channel',
                            operator: 'is_empty',
                            values: [],
                        },
                    ],
                },
            ],
        },
    },
};
